import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Box, Button, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

const BG = '#0a0a0a';
const BG_CARD = '#111111';
const BG_SURFACE = '#161616';
const ELECTRIC_BLUE = '#2196f3';
const ORANGE = '#ff9800';
const GREEN = '#00e676';
const TEXT = '#e5e5e5';
const TEXT_DIM = '#8f8f8f';
const MONO = '"JetBrains Mono", monospace';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: BG, paper: BG_CARD },
    primary: { main: ELECTRIC_BLUE },
    text: { primary: TEXT, secondary: TEXT_DIM },
  },
  typography: { fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' },
});

function Section({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return <Box component="section" sx={{ py: { xs: 8, md: 12 }, ...sx }}><Container maxWidth="lg">{children}</Container></Box>;
}

function InfoCard({ title, body }: { title: string; body: string }) {
  return (
    <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.12rem', mb: 1.25 }}>{title}</Typography>
      <Typography sx={{ color: TEXT_DIM, lineHeight: 1.75 }}>{body}</Typography>
    </Box>
  );
}

export default function ForPartnersPage() {
  const router = useRouter();

  return (
    <ThemeProvider theme={darkTheme}>
      <Head>
        <title>Aithon — Your Storefront. Your Brand. Your Catalog.</title>
        <meta name="description" content="Launch your own B2B services storefront on Aithon. Bring your traffic. Compete with visible perks. Pay only when deals close." />
        <link rel="canonical" href="https://aithon.tech/for-partners" />
        <meta property="og:title" content="Aithon — Your Storefront. Your Brand. Your Catalog." />
        <meta property="og:description" content="Launch a branded storefront on Aithon, show real pricing, compete with visible perks, and pay only when value changes hands." />
      </Head>
      <Box sx={{ bgcolor: BG, minHeight: '100vh', color: TEXT }}>
        <Box sx={{ pt: { xs: 12, md: 18 }, pb: { xs: 8, md: 14 }, background: 'radial-gradient(ellipse at 25% 20%, rgba(33,150,243,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(255,152,0,0.06) 0%, transparent 50%)' }}>
          <Container maxWidth="lg">
            <Chip label="FOR PARTNERS" sx={{ mb: 3, bgcolor: 'rgba(255,152,0,0.1)', color: ORANGE, border: '1px solid rgba(255,152,0,0.3)', fontWeight: 700 }} />
            <Typography variant="h1" sx={{ fontWeight: 900, fontSize: { xs: '2.5rem', md: '4.2rem' }, lineHeight: 1.08, mb: 3, maxWidth: 900 }}>
              Launch your own B2B services storefront. Bring your traffic. Close more deals.
            </Typography>
            <Typography sx={{ color: TEXT_DIM, fontSize: { xs: '1.08rem', md: '1.28rem' }, lineHeight: 1.7, maxWidth: 880, mb: 5 }}>
              List your services, show real pricing, and win customers with perks instead of back-channel politics.
              Your brand. Your catalog. Your customer relationships. Aithon handles the transaction layer.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => router.push('/registerpartner')}>
                Register as a Partner
              </Button>
              <Button variant="outlined" size="large" onClick={() => router.push('/how-aithon-works')} sx={{ color: TEXT, borderColor: 'rgba(255,255,255,0.2)' }}>
                See How It Works
              </Button>
            </Stack>
          </Container>
        </Box>

        <Section>
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.55rem' }, mb: 4 }}>
            The three things partners ask
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}><InfoCard title="Do I keep my brand?" body="Yes. Your branded storefront. Your domain. Your service catalog. Your customer relationship. You are not a commodity inside someone else’s platform." /></Grid>
            <Grid item xs={12} md={4}><InfoCard title="How do I win?" body="Visible pricing. Meaningful perks. Trust Score earned by real performance. The provider with the best offer wins the top listing. Competition happens in the open." /></Grid>
            <Grid item xs={12} md={4}><InfoCard title="What do I pay?" body="$50 one-time setup. 5% success fee when deals close. No monthly fees. No media spend. No pay-per-click tax. You pay when value changes hands. That’s it." /></Grid>
          </Grid>
        </Section>

        <Section sx={{ bgcolor: BG_SURFACE }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.45rem' }, mb: 3 }}>
                Your path on Aithon
              </Typography>
              <Stack spacing={1.65}>
                {[
                  'Launch your storefront — branded, your domain',
                  'List your services — with visible, real pricing',
                  'Attach meaningful perks — the value that differentiates you',
                  'Drive your existing traffic — to a storefront that converts better',
                  'Win the top listing — scored by value delivered, not politics',
                  'Earn recurring commission — on every renewal, compounding over time',
                ].map((item, index) => (
                  <Stack key={item} direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ minWidth: 28, height: 28, borderRadius: '50%', bgcolor: 'rgba(33,150,243,0.14)', color: ELECTRIC_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.9rem' }}>{index + 1}</Box>
                    <Typography sx={{ color: TEXT, lineHeight: 1.7 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ color: GREEN, fontWeight: 700, mb: 1.5 }}>Why early partners win</Typography>
                <Stack spacing={1.4}>
                  {[
                    'Early listings build Trust Score before the market gets crowded',
                    'Early performance builds ranking advantage that compounds',
                    'Early customer relationships create repeat and renewal revenue',
                    'As buyer traffic and AI agent demand compound, late entrants fight uphill against established reputations',
                  ].map((item) => <Typography key={item} sx={{ color: TEXT_DIM, lineHeight: 1.7 }}>• {item}</Typography>)}
                </Stack>
                <Typography sx={{ color: '#fff', fontWeight: 700, mt: 2.5 }}>The window for structural advantage is now.</Typography>
              </Box>
            </Grid>
          </Grid>
        </Section>

        <Section>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.45rem' }, mb: 3 }}>
                The math on one deal
              </Typography>
              <Typography sx={{ color: TEXT_DIM, lineHeight: 1.8, mb: 3 }}>
                One strong contract can fund a lot more than visibility. This is why the pay-per-closed-sale model matters.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: BG_SURFACE, border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography sx={{ fontFamily: MONO, color: ORANGE, fontSize: '0.82rem', letterSpacing: 1, textTransform: 'uppercase', mb: 2.5 }}>One $10k/mo deal</Typography>
                {[
                  ['Service price', '$10,000/mo'],
                  ['Commission (15% residual)', '$1,500/mo'],
                  ['Aithon keeps (10%)', '-$150/mo'],
                  ['You keep', '$1,350/mo'],
                  ['Over 3 years', '$48,600'],
                ].map(([label, value], index) => (
                  <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 1.35, borderBottom: index < 4 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                    <Typography sx={{ color: TEXT_DIM }}>{label}</Typography>
                    <Typography sx={{ color: index >= 3 ? GREEN : TEXT, fontFamily: MONO, fontWeight: index >= 3 ? 800 : 500 }}>{value}</Typography>
                  </Stack>
                ))}
                <Typography sx={{ color: TEXT_DIM, lineHeight: 1.7, mt: 2.25 }}>
                  And every renewal triggers new perk competition. Your book of business grows monotonically.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Section>
      </Box>
    </ThemeProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=7200');
  return { props: {} };
};
